import { useCallback, useContext, useRef } from "react";
import { NotificationContext } from "../utils/common";
import { getNotification } from "../redux/action/masterAction";
import {
  NOTIFICATION_PAGE_SIZE,
  emptyNotificationMeta,
  mergeNotificationPages,
  markNotificationViewedInList,
} from "../utils/notificationPagination";

export function useNotificationFeed() {
  const {
    notifications,
    setNotifications,
    notificationMeta,
    setNotificationMeta,
  } = useContext(NotificationContext) || {
    notifications: [],
    setNotifications: () => {},
    notificationMeta: emptyNotificationMeta,
    setNotificationMeta: () => {},
  };

  const meta = notificationMeta || emptyNotificationMeta;
  const loadMoreLock = useRef(false);

  const applyResult = useCallback(
    (result, { append } = {}) => {
      let added = true;
      if (typeof setNotifications === "function") {
        setNotifications((prev) => {
          if (!append) return result.items;
          const merged = mergeNotificationPages(prev, result.items);
          added = merged.length > (Array.isArray(prev) ? prev.length : 0);
          return merged;
        });
      }
      if (typeof setNotificationMeta === "function") {
        setNotificationMeta((prev) => ({
          ...(prev || emptyNotificationMeta),
          page: result.page,
          hasMore: append ? Boolean(result.hasMore && added) : result.hasMore,
          total: result.total,
          unreadCount:
            result.unreadCount != null
              ? result.unreadCount
              : prev?.unreadCount ?? null,
          loading: false,
          loadingMore: false,
        }));
      }
    },
    [setNotifications, setNotificationMeta],
  );

  const refresh = useCallback(async () => {
    if (typeof setNotificationMeta === "function") {
      setNotificationMeta((prev) => ({
        ...(prev || emptyNotificationMeta),
        loading: true,
      }));
    }
    const result = await getNotification({
      page: 1,
      limit: NOTIFICATION_PAGE_SIZE,
    });
    applyResult(result, { append: false });
    return result;
  }, [applyResult, setNotificationMeta]);

  const loadMore = useCallback(async () => {
    if (loadMoreLock.current) return;
    if (meta.loading || meta.loadingMore || meta.hasMore === false) return;
    loadMoreLock.current = true;
    if (typeof setNotificationMeta === "function") {
      setNotificationMeta((prev) => ({
        ...(prev || emptyNotificationMeta),
        loadingMore: true,
      }));
    }
    try {
      const nextPage = (Number(meta.page) || 0) + 1;
      const result = await getNotification({
        page: nextPage,
        limit: NOTIFICATION_PAGE_SIZE,
      });
      applyResult(result, { append: true });
      return result;
    } finally {
      loadMoreLock.current = false;
    }
  }, [applyResult, meta.hasMore, meta.loading, meta.loadingMore, meta.page, setNotificationMeta]);

  const markReadLocal = useCallback(
    (notificationId) => {
      if (typeof setNotifications === "function") {
        setNotifications((prev) =>
          markNotificationViewedInList(prev, notificationId),
        );
      }
      if (typeof setNotificationMeta === "function") {
        setNotificationMeta((prev) => {
          const current = prev || emptyNotificationMeta;
          const unread =
            current.unreadCount == null
              ? current.unreadCount
              : Math.max(0, Number(current.unreadCount) - 1);
          return { ...current, unreadCount: unread };
        });
      }
    },
    [setNotifications, setNotificationMeta],
  );

  return {
    notifications,
    refresh,
    loadMore,
    markReadLocal,
    hasMore: Boolean(meta.hasMore),
    loading: Boolean(meta.loading),
    loadingMore: Boolean(meta.loadingMore),
    unreadCount: meta.unreadCount,
    total: meta.total,
  };
}
